import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plane, Mail, Check, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { api } from "../lib/api";
import { ASSETS, CONTACT_EMAIL } from "../data/safety";

export default function About() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", topic: "allgemein", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t.about.form.ok);
      setForm({ name: "", email: "", topic: "allgemein", message: "" });
    } catch { toast.error("Fehler / Error"); }
  };

  return (
    <div>
      {/* Story */}
      <section className="mx-auto max-w-6xl px-4 py-12 md:py-20 grid gap-10 md:grid-cols-2 md:items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-2 instrument-label text-sonne"><Plane size={14} /> {t.about.kicker}</div>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">{t.about.title}</h1>
          <div className="mt-6 space-y-4 text-wachs-dim leading-relaxed">
            {t.about.body.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
          <div className="overflow-hidden rounded-xl border border-bronze/25">
            <img src={ASSETS.founder} alt="Circle of Ikarus" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-4 -left-4 hidden md:block">
            <img src={ASSETS.logo} alt="" className="h-20 w-auto object-contain drop-shadow-lg" />
          </div>
        </motion.div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <div className="flex items-center gap-2 instrument-label text-sonne"><Mail size={14} /> {t.about.contactTitle}</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">{t.about.contactTitle}</h2>
            <p className="mt-3 text-wachs-dim">{t.about.contactSub}</p>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email" className="mt-5 inline-flex items-center gap-2 font-mono text-sm text-bronze-soft hover:text-sonne transition-colors">
              <Mail size={15} /> {CONTACT_EMAIL}
            </a>
            <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-2 font-mono text-sm text-wachs-dim hover:text-sonne transition-colors">
              <Linkedin size={15} /> LinkedIn
            </a>
          </div>

          <div className="grid-panel rounded-xl p-6 md:p-8">
            {sent ? (
              <div className="flex items-center gap-2 text-ready" data-testid="contact-success"><Check size={18} /> {t.about.form.ok}</div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="instrument-label text-wachs-dim">{t.about.form.name}</span>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="coi-input mt-1.5" />
                  </label>
                  <label className="block">
                    <span className="instrument-label text-wachs-dim">{t.about.form.email}</span>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className="coi-input mt-1.5" />
                  </label>
                </div>
                <label className="block">
                  <span className="instrument-label text-wachs-dim">{t.about.form.topic}</span>
                  <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic" className="coi-input mt-1.5">
                    {Object.entries(t.about.topics).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="instrument-label text-wachs-dim">{t.about.form.message}</span>
                  <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="coi-input mt-1.5 resize-none" />
                </label>
                <button type="submit" data-testid="contact-submit" className="bg-bronze text-nacht font-medium px-6 py-3 rounded-md hover:bg-bronze-soft transition-colors">
                  {t.about.form.send}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
